<script>
import FormGroup from '@maison/components/form/FormGroup.vue';
import TextField from '@maison/components/form/fields/TextField.vue';
import PasswordField from '@maison/components/form/fields/PasswordField';

import LoginFormWithGemini from '@phoenix/mixins/components/form/LoginFormWithGemini';
import { defineAsyncComponent } from 'vue';

export default {
  components: {
    FormGroup,
    PhoenixForm: defineAsyncComponent(() => import('@maison/components/form/PhoenixForm.vue')),
    TextField,
    PasswordField,
    PhoneTokenField: defineAsyncComponent(() => import('@maison/components/form/fields/PhoneTokenField')),
    SendPhoneTokenForm: defineAsyncComponent(() => import('@maison/components/myAccount/SendPhoneTokenForm')),
  },

  mixins: [LoginFormWithGemini],

  props: {
    customSubmitButtonClasses: {
      type: String,
      default: null,
    },
    isSubmitOnLeft: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['formLoaded'],

  computed: {
    nextButtonClass() {
      return ['btn btn--default btn--full btn--negative', { 'btn--disabled': this.isNextButtonDisabled }];
    },
  },

  methods: {
    getRetrievePasswordLinkClass() {
      return 'link link--text link--accent text-sm';
    },

    getPasswordHelperDataTracking() {
      return window.tracking.getPasswordForgotTracking();
    },
  },
};
</script>

<template>
  <div>
    <send-phone-token-form
      v-if="showTokenOrPasswordButtons"
      ref="sendPhoneTokenForm"
      :mobile-phone="fields.emailOrPhone.value"
      :is-boxed="false"
      :show-errors-in-toast="shouldShowErrorInToast"
      :should-display-chinese-pipl-statement="true"
      type="login"
      @on-success="sendTokenSuccess"
      @on-fail="sendTokenFail"
    >
      <div class="flex-grid flex-grid--y--base flex-grid--x--base justify-around">
        <form-group :attrs="fields.emailOrPhone" class="w-full" data-cy="login-form-email">
          <text-field
            v-model="fields.emailOrPhone.value"
            :placeholder="fields.emailOrPhone.placeholder"
            :should-validate="false"
          />
          <template v-if="fields.emailOrPhone.disabled" #help>
            <div class="field-help">
              <button type="button" class="btn btn--bare btn--link" @click.prevent.stop="back">
                {{ $t('MyAccount.goBack') }}
              </button>
            </div>
          </template>
        </form-group>
      </div>

      <template #form-actions>
        <button
          ref="sendTokenPhoneButton"
          type="button"
          class="btn btn--default btn--full btn--negative"
          data-cy="use-token-btn"
          @click.prevent="sendToken"
        >
          <span class="btn__text">
            {{ $t('MyAccount.sendPhoneToken') }}
          </span>
        </button>
        <p class="text-center my-base">
          {{ $t('Form.or') }}
        </p>
        <button class="btn btn--default btn--full" data-cy="use-password-btn" @click.prevent.stop="usePassword">
          <span class="btn__text">
            {{ $t('MyAccount.usePassword') }}
          </span>
        </button>
      </template>
    </send-phone-token-form>

    <phoenix-form
      v-else
      ref="phoenixForm"
      v-model="fields"
      :action="formAction"
      :prefill-enabled="true"
      :redirect-url="redirectUrl"
      :is-contained="false"
      :show-errors-in-toast="shouldShowErrorInToast"
      :should-display-required-text="false"
      :should-display-submit-button="showLoginButton"
      :custom-submit-text="$t('MyAccount.login')"
      :custom-submit-button-classes="customSubmitButtonClasses"
      :is-submit-on-left="isSubmitOnLeft"
      :on-success-callback="addTrackingInformation"
      data-cy="main-login-form"
      @form-loaded="$emit('formLoaded')"
    >
      <div class="flex-grid flex-grid--y--base flex-grid--x--base justify-around">
        <form-group :attrs="fields.emailOrPhone" class="w-full" data-cy="login-form-email">
          <text-field v-model="fields.emailOrPhone.value" :placeholder="fields.emailOrPhone.placeholder" />
          <template v-if="fields.emailOrPhone.disabled" #help>
            <div class="field-help">
              <button type="button" class="btn btn--bare btn--link" @click.prevent.stop="back">
                {{ $t('MyAccount.goBack') }}
              </button>
            </div>
          </template>
        </form-group>

        <form-group v-if="showPasswordField" :attrs="fields.password" class="w-full" data-cy="login-form-password">
          <password-field v-model="fields.password.value" />
        </form-group>

        <div v-if="showTokenField">
          <p v-if="hasPhoneTokenTooManyRetries" class="field-error">
            {{ $t('Authentication.phone.token.retryIn', { timeLeft: timeLeftBeforeTokenRetry }) }}
          </p>
          <div v-else>
            <form-group :attrs="fields.phoneToken" class="w-full" data-cy="login-form-token">
              <phone-token-field v-model="fields.phoneToken.value" @resend-phone-token="backToPhoneStep" />
            </form-group>
          </div>
        </div>
      </div>

      <template #form-actions="slotProps">
        <button
          v-if="showNextButton"
          :class="nextButtonClass"
          :disabled="isNextButtonDisabled || slotProps.isDisabled"
          data-cy="next-btn"
          @click.prevent.stop="next"
        >
          <span class="btn__text">
            {{ $t('MyAccount.loginNext') }}
          </span>
        </button>
      </template>
    </phoenix-form>
  </div>
</template>
